import { api } from './api'

export interface PushPrefs {
  gameStarted: boolean
  gameCompleted: boolean
  invited: boolean
}

export interface PushPrefsResponse extends PushPrefs {
  subscribed: boolean
}

export function pushSupported(): boolean {
  return 'PushManager' in window && 'serviceWorker' in navigator && 'Notification' in window
}

/** iPhone/iPad only get Web Push when the app is launched from the home screen. */
export function needsHomeScreenInstall(): boolean {
  const ua = navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true
  return isIos && !standalone
}

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const base64 = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export async function getVapidKey(): Promise<string> {
  const response = await api.get<{ key: string }>('/push/vapid-public-key')
  return response.data.key
}

/** The subscription this browser currently holds, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null
  const registration = await navigator.serviceWorker.ready
  return registration.pushManager.getSubscription()
}

/** Asks for permission, subscribes this device and registers it with the server. */
export async function subscribePush(): Promise<void> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('permission-denied')
  }
  const key = await getVapidKey()
  const registration = await navigator.serviceWorker.ready
  let subscription = await registration.pushManager.getSubscription()
  if (subscription) {
    const currentKey = subscription.options.applicationServerKey
    const expected = urlBase64ToUint8Array(key)
    const sameKey =
      currentKey &&
      new Uint8Array(currentKey).length === expected.length &&
      new Uint8Array(currentKey).every((b, i) => b === expected[i])
    if (!sameKey) {
      await subscription.unsubscribe()
      subscription = null
    }
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key)
    })
  }
  await api.post('/push/subscribe', { subscription: subscription.toJSON() })
}

/** Removes this device's subscription both locally and on the server. */
export async function unsubscribePush(): Promise<void> {
  const subscription = await currentSubscription()
  if (!subscription) return
  const { endpoint } = subscription
  await subscription.unsubscribe()
  await api.delete('/push/subscribe', { data: { endpoint } })
}

export async function getPrefs(): Promise<PushPrefsResponse> {
  const response = await api.get<PushPrefsResponse>('/push/prefs')
  return response.data
}

export async function setPrefs(prefs: Partial<PushPrefs>): Promise<void> {
  await api.patch('/push/prefs', prefs)
}

export async function sendTestPush(): Promise<number> {
  const response = await api.post<{ sent: number }>('/push/test')
  return response.data.sent
}
